import { FC, ReactNode, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useWbTabsContext } from './WbTabsContext';
import { TabValue } from './types';

const useStyles = makeStyles(
  {
    root: {
      outline: 'none',
    },
  },
  { name: 'WbTabPanel' },
);

interface WbTabPanelProps {
  children: ReactNode;
  value: TabValue;
  className?: string;
  focusable?: boolean;
  /** Keep children mounted in the DOM even when the panel is not selected. */
  keepMounted?: boolean;
}

export const WbTabPanel: FC<WbTabPanelProps> = ({
  children,
  value,
  className,
  focusable = false,
  keepMounted = false,
}) => {
  const classes = useStyles();
  const { value: selectedValue, instanceId, getPanelId } = useWbTabsContext();
  const isSelected = selectedValue === value;
  const hasMountedRef = useRef(isSelected);

  if (isSelected) {
    hasMountedRef.current = true;
  }

  const shouldRenderChildren = keepMounted ? hasMountedRef.current : isSelected;

  return (
    <div
      id={getPanelId(value)}
      role="tabpanel"
      aria-labelledby={`${instanceId}-tab-${value}`}
      aria-hidden={!isSelected}
      tabIndex={focusable ? 0 : undefined}
      className={clsx(classes.root, className)}
      style={!isSelected ? { display: 'none' } : undefined}
    >
      {shouldRenderChildren && children}
    </div>
  );
};
